// Audience-filter parsing, shared by the admin tables.
//
// Lived in routes/admin/AdminOrders.tsx until ATAD-132 gave the activations
// table a second use for it. It moved rather than being imported across,
// because AdminOrders imports ActivationsTable — the reverse import would
// have closed a cycle.

// PR 2d: parser is AST-aware. Walks `variables.expression` /
// `variables.excludeExpression` when present (post-PR-2b-1 audiences),
// falls back to the legacy `variables.include[]` / `variables.exclude[]`
// arrays for older audiences. Membership leaves (audience / file kinds)
// are skipped — they aren't variables.
//
// Exported so a focused unit test can pin both shapes.
export const extractVariablesFromAudienceFilters = (
  filtersString: string | null,
): { included: string[]; excluded: string[] } => {
  if (!filtersString) return { included: [], excluded: [] };

  try {
    const filters = JSON.parse(filtersString);
    const included: string[] = [];
    const excluded: string[] = [];

    const v = filters.variables ?? {};
    const isAstShape =
      'expression' in v || 'excludeExpression' in v;

    if (isAstShape) {
      collectVariableKeys(v.expression, included);
      collectVariableKeys(v.excludeExpression, excluded);
    } else {
      // Legacy flat-array shape (audiences saved before the AND/OR feature).
      (v.include ?? []).forEach((f: { id?: string; key?: string }) => {
        const value = f.key || f.id;
        if (value && !included.includes(value)) included.push(value);
      });
      (v.exclude ?? []).forEach((f: { id?: string; key?: string }) => {
        const value = f.key || f.id;
        if (value && !excluded.includes(value)) excluded.push(value);
      });
    }

    return { included, excluded };
  } catch (err) {
    console.error('Error parsing audience filters:', err);
    return { included: [], excluded: [] };
  }
};

/**
 * Walk an AST expression node and push every variable leaf's `key` onto
 * `out`. Membership leaves (kind 'audience', 'file', 'zip' or 'model') are
 * skipped — they aren't variables and don't belong in the "Variables
 * Included" column. ATAD-146: so is any other non-variable kind, so a kind
 * added later can't land in that column by accident.
 */
function collectVariableKeys(node: any, out: string[]): void {
  if (!node) return;
  if (node.type === 'leaf') {
    if (node.kind != null && node.kind !== 'variable') return;
    const key = node.key;
    if (key && !out.includes(key)) out.push(key);
    return;
  }
  (node.children || []).forEach((c: any) => collectVariableKeys(c, out));
}
